import { formatCurrency } from 'common/FormatUtilities';
import { urlDateFormat } from 'common/UrlDate';
import { isNil } from 'lodash';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect } from 'react';
import { RouteChildrenProps } from 'react-router';
import { ConsumerFoodItemFragment, PublicShef } from 'src/gqlReactTypings.generated.d';
import { MAIN_FOOD_TYPES } from 'src/shared/Constants';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'src/shared/design-system/Modal';
import { Paragraph } from 'src/shared/design-system/Text';
import { useDispatch } from 'src/store';
import { incLineItemQuantity } from 'src/store/cart/actions';
import { CartFoodItem } from 'src/store/cart/types';
import styled from 'styled-components';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { Colors } from 'web-common/src/shared/styles';
import { useCachedZipCode } from '../../../../user-preferences/useCachedZipCode';
import { ConsumerOrderDishSelectionItem, ConsumerOrderDishSelectionItemFormat } from './ConsumerOrderDishSelectionItem';

const FreeDeliveryModal = styled(Modal)`
  width: 600px;

  & > ${ModalHeader} {
    padding: 20px 15px 0;
    flex-direction: column;
  }
`;

const ModalSubmitButton = styled.button`
  background-color: ${Colors.RADISH};
  border: none !important;
  color: white;
  font-size: 18px;
  padding: 10px;
`;

const ProgressBar = styled.div`
  width: 90%;
  margin: 0 auto;
  height: 20px;
  background-color: ${Colors.GRAY_LITE_10};
  border-radius: 4px;
`;

const Progress = styled.div<{ width: number }>`
  height: 20px;
  background-color: ${Colors.AQUA};
  border-radius: 4px;
  width: ${(props) => props.width}%;
  max-width: 100%;
`;

const RecommendedDishes = styled.div`
  padding: 0 15px;
  min-height: 330px;
  display: flex;
  & > div {
    padding: 0;
  }
  & > div:first-child {
    padding-right: 16px;
  }
`;

interface IProps {
  shef: Pick<PublicShef, 'publicName' | 'firstName'>;
  deliveryDate: DateTime;
  foodItems: ConsumerFoodItemFragment[];
  subtotal: number;
  checkout: (checkout?: boolean) => any;
  match: RouteChildrenProps['match'];
  location: RouteChildrenProps['location'];
  onHide: () => any;
  show?: boolean;
}

export const ConsumerOrderFreeDeliveryModal: React.FC<IProps> = ({
  show,
  onHide,
  shef,
  subtotal,
  checkout,
  match,
  location,
  foodItems,
  deliveryDate,
}) => {
  const { isSmallScreen } = useScreenContext();
  const [, , zipCodeMetaData] = useCachedZipCode();
  const { minimumDiscountedShippingAmount, discountedDeliveryFee } = zipCodeMetaData;
  const dispatch = useDispatch();
  const deliveryUrlDate = deliveryDate.toFormat(urlDateFormat);
  const increment = useCallback(
    (foodItem: CartFoodItem) => {
      dispatch(incLineItemQuantity(false, { foodItem, deliveryDate: deliveryUrlDate, location: 'other' }));
    },
    [dispatch, deliveryUrlDate]
  );

  const recommendedDishes = foodItems
    .filter((foodItem) => foodItem && MAIN_FOOD_TYPES.includes(foodItem.foodType))
    .map((foodItem) => {
      const availabilityForDate = foodItem.availability.find(
        (availability) => new Date(availability.availabilityDate).getTime() === deliveryDate.toMillis()
      );
      return {
        foodItem,
        availabilityForDate,
      };
    })
    .filter(
      (item) =>
        item.availabilityForDate &&
        (isNil(item.availabilityForDate.numAvailable) || item.availabilityForDate.numAvailable > 0)
    )
    .slice(0, 2)
    .map(({ foodItem, availabilityForDate }) => (
      <ConsumerOrderDishSelectionItem
        showDishTags={false}
        remainingQuantity={availabilityForDate ? availabilityForDate.numAvailable : 0}
        key={foodItem.id}
        foodItem={foodItem}
        format={ConsumerOrderDishSelectionItemFormat.FREE_DELIVERY_MODAL}
        quantity={0}
        increment={increment}
        checkout={checkout}
        subtotal={subtotal}
        match={match}
        location={location}
        isEditingOrder={false}
      />
    ));

  const modalWillShow = Boolean(show && recommendedDishes && recommendedDishes.length);

  // Prevents bug where we wanted to show a modal, but the modal itself does not
  // want to be shown. If the parent component which renders this is expecting
  // onHide to be called, they will be waiting forever, because nothing will ever
  // call it.
  useEffect(() => {
    if (show && !modalWillShow) {
      console.log('Free delivery modal could not be shown!');
      onHide();
    }
  }, [modalWillShow, show, onHide]);

  if (minimumDiscountedShippingAmount === null) {
    return null;
  }

  const progressWidth = (subtotal / minimumDiscountedShippingAmount) * 100;
  const formattedDiscount = discountedDeliveryFee === 0 ? 'free' : formatCurrency(discountedDeliveryFee);
  const meetTheMinimumAmount = formatCurrency(minimumDiscountedShippingAmount - subtotal);
  return (
    <FreeDeliveryModal
      isShowing={modalWillShow}
      onClose={onHide}
      showCloseButton
      isFullWidth={isSmallScreen}
      isFullHeight={isSmallScreen}
      isRounded={false}>
      <ModalHeader>
        <Paragraph size='26px' bold>
          Want {formattedDiscount} delivery?
        </Paragraph>

        <Paragraph size='18px' bold>
          Just add <span style={{ color: Colors.RADISH }}>{meetTheMinimumAmount}</span> from {shef.publicName}!
        </Paragraph>
        <ProgressBar>
          <Progress width={progressWidth}></Progress>
        </ProgressBar>
      </ModalHeader>

      <ModalBody>
        <Paragraph size='18px' margin='0 0 24px' bold style={{ textAlign: 'center' }}>
          We recommend these dishes
        </Paragraph>
        <RecommendedDishes>{recommendedDishes}</RecommendedDishes>
      </ModalBody>

      <ModalFooter>
        <ModalSubmitButton type='submit' onClick={() => checkout(true)}>
          Continue to checkout
        </ModalSubmitButton>
      </ModalFooter>
    </FreeDeliveryModal>
  );
};
